import { type RefObject } from 'react';

import { importPromise, type ImportPromiseCta } from '@/content/importPromise';
import { type ContentItem } from '@/content/site';

import { useSheenOnEnter } from './useSheenOnEnter';

/**
 * What a page may say differently. Every field is optional and every one
 * defaults to the site-wide copy, because the band's value is that it is the
 * same sentence everywhere (see `content/importPromise.ts`); these exist for
 * the page that one day has a reason to differ, not as an invitation.
 */
export type ImportPromiseOverrides = {
  eyebrow?: string;
  title?: string;
  text?: string;
  proofs?: readonly ContentItem[];
  cta?: ImportPromiseCta;
};

export type ImportPromiseModel = ImportPromiseOverrides & {
  eyebrow: string;
  title: string;
  text: string;
  proofs: readonly ContentItem[];
  cta: ImportPromiseCta;
  /** Target of the band's `aria-labelledby`; fixed, as the band is unique per page. */
  titleId: string;
  /**
   * Attach to the element whose arrival triggers the sheen.
   *
   * Written out rather than inferred from `useSheenOnEnter`: that hook is
   * generic, so `ReturnType` instantiates it at the constraint (`HTMLElement`)
   * instead of at its default, and the ref stops being assignable to the
   * `div` it is actually put on.
   */
  sheenRef: RefObject<HTMLDivElement | null>;
  /** True once that element has been scrolled to, and motion is not reduced. */
  playSheen: boolean;
};

/**
 * Everything `ImportPromiseBand` needs to think about: which copy it is
 * showing, and whether the sheen has been triggered yet.
 *
 * Both live here rather than in the organism so the organism is layout and
 * nothing else — it holds no state, reads no media query and resolves no
 * defaults. That also makes the band's behaviour testable without rendering
 * MUI, and lets a future variant (a narrower band inside a page, say) reuse
 * the same resolution with different structure.
 */
export function useImportPromise(overrides: ImportPromiseOverrides = {}): ImportPromiseModel {
  const { ref, play } = useSheenOnEnter<HTMLDivElement>();

  return {
    eyebrow: overrides.eyebrow ?? importPromise.eyebrow,
    title: overrides.title ?? importPromise.title,
    text: overrides.text ?? importPromise.text,
    proofs: overrides.proofs ?? importPromise.proofs,
    cta: overrides.cta ?? importPromise.cta,
    titleId: 'import-promise-title',
    sheenRef: ref,
    playSheen: play,
  };
}
